import { BottomSheet } from "@/design-system/BottomSheet/BottomSheet";
import { Button } from "@/design-system/Button/Button";
import { Levels } from "@/design-system/Evolution/Levels";
import { useUpgradeFormatter } from "@/features/Evolutions/useUpgradeFormatter";
import {
  applyEvolutionStep,
  closePathwayDraft,
  evolutionsBuilderSelector,
  fetchPathwayOptions,
  getCurrentEvolutionPlayerSelector,
  selectPathway,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import { getEvolutionByIdSelector } from "@/redux/evolutions/evolutionsSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import {
  isPathwayOptionAvailable,
  pathwayNeighborFor,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import type { EvolutionStep } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { EvoStepCard } from "./EvoStepCard";

/**
 * "Choose your pathway": opens for a pathway evolution clicked in the one-step
 * grid. The level cards are the overview page's, choice only, with their
 * path picker; every path other than the selected one maps to a neighbor entry
 * from pathway-options. Hovering a path previews it, choosing it selects it
 * and asks for the next set of neighbors. The chain only changes on "Add to
 * chain".
 */
export const PathwaySheet = ({
  playerRoleIds,
  onAddToChain,
}: {
  playerRoleIds: number[];
  onAddToChain: (step: EvolutionStep) => void;
}) => {
  const { t } = useTranslation("evoBuilder");
  const { t: tCommon } = useTranslation("common");
  const dispatch = useDispatch<AppDispatch>();
  const formatUpgrade = useUpgradeFormatter();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const { pathwayDraft: draft } = useSelector(evolutionsBuilderSelector);
  const currentPlayer = useSelector(getCurrentEvolutionPlayerSelector);
  const evolution = useSelector(
    getEvolutionByIdSelector(draft?.gridStep.evolutionId),
  );
  const [previewed, setPreviewed] = useState<EvolutionStep>();

  useEffect(() => {
    if (draft) dispatch(fetchPathwayOptions({ playerRoleIds }));
  }, [draft?.pathway]);

  // A new set of neighbors invalidates whatever was being previewed.
  useEffect(() => {
    setPreviewed(undefined);
  }, [draft?.neighbors]);

  if (!draft || !evolution) return null;

  const recommended = draft.gridStep.pathwayPath ?? [];

  // The card previews the hovered path; the picker keeps showing the chosen
  // one until a path is actually clicked.
  const shownStep = previewed ?? draft.selectedStep;
  const close = () => dispatch(closePathwayDraft());

  const choose = (pathway: number[]) => {
    setPreviewed(undefined);
    dispatch(selectPathway(pathway));
  };

  return (
    <BottomSheet
      isOpen
      onClose={close}
      // The page background, not the sheet's default surface: the cards
      // inside need the darker ground to read as cards.
      className="h-[90vh] bg-background touch-device:h-[85vh]"
      header={{
        title: t("pathwaySheet.title"),
        closeLabel: tCommon("close"),
        action: (
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              onAddToChain(draft.selectedStep);
              close();
            }}
          >
            {t("pathwaySheet.addToChain")}
          </Button>
        ),
      }}
    >
      <div className="mx-auto flex w-full max-w-content flex-col gap-8 px-4 pb-8 large:px-16 touch-device:px-4">
        <div className="w-full max-w-2xl">
          <EvoStepCard
            {...shownStep}
            size="large"
            player={applyEvolutionStep(currentPlayer, shownStep)}
            metaRatingBeforeApplyingUpgrade={currentPlayer.metaRating}
            playerRoleIdBeforeApplyingUpgrade={currentPlayer.playerRoleId}
            loading={false}
            onClick={() => {}}
            className="pointer-events-none"
          />
        </div>
        <Levels
          evolution={evolution}
          pathwayPath={draft.pathway}
          recommendedPath={recommended}
          onChange={choose}
          isOptionAvailable={(level, option) =>
            isPathwayOptionAvailable(
              draft.pathway,
              draft.neighbors,
              level,
              option,
            )
          }
          onPreview={(pathway) =>
            setPreviewed(
              pathway && pathway.join() !== draft.pathway.join()
                ? pathwayNeighborFor(draft.neighbors, pathway)
                : undefined,
            )
          }
          loading={draft.loading}
          layout={isMobile ? "scroll" : "wrap"}
          formatUpgrade={formatUpgrade}
          // The card above already shows what this path does to *this*
          // player; the level cards' generic numbers would contradict it.
          choiceOnly
        />
      </div>
    </BottomSheet>
  );
};
